import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Wand2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import Modal from "@/components/app/Modal";
import Toggle from "@/components/app/Toggle";
import { Field } from "@/components/app/FormKit";
import { btnOutline, btnPrimary, labelCls, inputCls } from "@/components/app/ui";
import SortableList from "@/components/homepage/SortableList";
import ImagePicker from "@/components/homepage/ImagePicker";
import StarRating from "@/components/homepage/StarRating";
import { NETWORKS, validSocialUrl } from "@/lib/social";

const key = () => Math.random().toString(36).slice(2, 10);
const withKeys = (items = []) => items.map((i) => ({ ...i, _key: i.id || key() }));
const strip = (items) => items.map(({ _key, _open, ...rest }) => rest);
const INSTAGRAM_RE = /^https:\/\/(www\.)?(instagram\.com|instagr\.am)\/(p|reel|reels|tv)\//i;

/* shared modal frame with Save / Cancel */
function EditorModal({ title, subtitle, onClose, onSave, saving, children, size = "lg", testId }) {
  return (
    <Modal open onClose={onClose} size={size} title={title} subtitle={subtitle} testId={testId}
      footer={<>
        <button onClick={onClose} className={btnOutline}>Cancel</button>
        <button onClick={onSave} disabled={saving} data-testid="homepage-save" className={btnPrimary}>{saving ? "Saving…" : "Save changes"}</button>
      </>}>
      {children}
    </Modal>
  );
}

function SectionToggle({ label, checked, onChange, hint }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2">
      <div><div className="text-[13px] font-semibold text-neutral-900">{label}</div>{hint && <div className="text-[11px] text-slate-500">{hint}</div>}</div>
      <Toggle checked={checked} onChange={onChange} label={label} t="section-active" />
    </div>
  );
}

// run a PUT, toast, close
async function persist(path, body, { onSaved, onClose, setSaving, ok }) {
  setSaving(true);
  try {
    const { data } = await api.put(`/vendor/homepage/${path}`, body);
    toast.success(ok);
    onSaved(data);
    onClose();
  } catch (err) {
    toast.error(apiErr(err.response?.data?.detail));
  } finally { setSaving(false); }
}

/* ------------------------------------------------------------------ Banners */
export function ReviewsEditor({ section, ctx, onSaved, onClose }) {
  const { limits, defaults } = ctx;
  const [title, setTitle] = useState(section?.title || "");
  const [active, setActive] = useState(section?.active ?? true);
  const [items, setItems] = useState(() => withKeys(section?.items));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const patch = (k, p) => setItems((all) => all.map((i) => (i._key === k ? { ...i, ...p } : i)));
  const add = () => { if (items.length >= limits.reviews) return toast.error(`Up to ${limits.reviews} reviews.`); setItems([...items, { _key: key(), name: "", message: "", rating: 5, image: "", active: true }]); };
  const save = () => {
    const e = {};
    items.forEach((it) => {
      const x = {};
      if (!it.name.trim()) x.name = "Customer name is required.";
      if (!it.message.trim()) x.message = "Write the review message.";
      if (!(it.rating >= 1 && it.rating <= 5)) x.rating = "Choose a rating from 1 to 5.";
      if (Object.keys(x).length) e[it._key] = x;
    });
    setErrors(e);
    if (Object.keys(e).length) return toast.error("Please fix the highlighted review fields.");
    persist("reviews", { title, active, items: strip(items) }, { onSaved, onClose, setSaving, ok: "Customer reviews saved." });
  };
  return (
    <EditorModal title="Customer reviews" subtitle="Testimonials shown on your homepage. These are entered by you and are separate from order data." onClose={onClose} onSave={save} saving={saving} size="xl" testId="editor-reviews">
      <div className="space-y-3">
        <SectionToggle label="Show this section" checked={active} onChange={setActive} hint="Hidden automatically when there is no active review." />
        <Field label="Section title" maxLength={60} placeholder={defaults.reviews} value={title} onChange={setTitle} t="section-title" />
        {items.length === 0 && <div className="rounded-lg border-2 border-dashed border-slate-300 py-6 text-center text-[13px] text-slate-500">No reviews yet.</div>}
        <SortableList items={items} onChange={setItems} keyOf={(i) => i._key} testId="review-list"
          renderItem={(it, idx, handle) => {
            const er = errors[it._key] || {};
            return (
              <div className={`rounded-lg border bg-white p-2.5 grid grid-cols-12 gap-x-3 gap-y-2 ${Object.keys(er).length ? "border-red-300" : "border-slate-200"}`}>
                <div className="col-span-12 sm:col-span-1 flex sm:block items-center">{handle}</div>
                <div className="col-span-12 sm:col-span-2"><ImagePicker value={it.image} onChange={(v) => patch(it._key, { image: v })} aspect="aspect-square" hint="Photo (optional)" /></div>
                <div className="col-span-12 sm:col-span-7 grid grid-cols-2 gap-x-3 gap-y-2 content-start">
                  <Field label="Customer name" required maxLength={60} value={it.name} onChange={(v) => patch(it._key, { name: v })} error={er.name} t={`review-name-${idx}`} />
                  <div><label className={labelCls}>Rating <span className="text-red-500">*</span></label><StarRating value={it.rating} onChange={(v) => patch(it._key, { rating: v })} />{er.rating && <p className="text-[11px] text-red-600">{er.rating}</p>}</div>
                  <Field className="col-span-2" label="Review message" required type="textarea" rows={2} maxLength={300} value={it.message} onChange={(v) => patch(it._key, { message: v })} error={er.message} t={`review-message-${idx}`} />
                </div>
                <div className="col-span-12 sm:col-span-2 flex sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-2">
                  <Toggle checked={it.active} onChange={(v) => patch(it._key, { active: v })} label="Active" />
                  <button type="button" onClick={() => { if (window.confirm("Delete this review?")) setItems(items.filter((i) => i._key !== it._key)); }} aria-label="Delete review" className="w-8 h-8 rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600 flex items-center justify-center"><Trash2 size={15} /></button>
                </div>
              </div>
            );
          }} />
        <button type="button" onClick={add} disabled={items.length >= limits.reviews} data-testid="review-add" className={btnOutline}><Plus size={14} /> Add review ({items.length}/{limits.reviews})</button>
      </div>
    </EditorModal>
  );
}

/* ---------------------------------------------------------------- Instagram */
export function InstagramEditor({ section, ctx, onSaved, onClose }) {
  const { limits, defaults } = ctx;
  const [title, setTitle] = useState(section?.title || "");
  const [active, setActive] = useState(section?.active ?? true);
  const [items, setItems] = useState(() => withKeys(section?.items));
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState({});
  const [saving, setSaving] = useState(false);
  const patch = (k, p) => setItems((all) => all.map((i) => (i._key === k ? { ...i, ...p } : i)));
  const add = () => { if (items.length >= limits.instagram) return toast.error(`Up to ${limits.instagram} posts.`); setItems([...items, { _key: key(), image: "", url: "", caption: "", active: true }]); };

  // Paste a public post link and we fetch the picture and caption for you.
  const lookup = async (k, rawUrl) => {
    const url = rawUrl.trim();
    if (!INSTAGRAM_RE.test(url)) { setErrors((e) => ({ ...e, [k]: { url: "Use a link like https://www.instagram.com/p/XXXX/" } })); return; }
    setBusy((b) => ({ ...b, [k]: true }));
    setErrors((e) => { const { [k]: _gone, ...rest } = e; return rest; });
    try {
      const { data } = await api.post("/vendor/homepage/instagram/lookup", { url });
      setItems((all) => all.map((i) => (i._key === k ? { ...i, url: data.post_url, image: data.image, caption: i.caption || data.caption } : i)));
      toast.success(data.caption ? "Post details fetched." : "Picture fetched. This post has no caption — add one if you like.");
    } catch (err) {
      const msg = apiErr(err.response?.data?.detail);
      setErrors((e) => ({ ...e, [k]: { url: msg } }));
      toast.error(msg);
    } finally { setBusy((b) => ({ ...b, [k]: false })); }
  };

  const save = () => {
    const e = {};
    items.forEach((it) => {
      const x = {};
      if (!it.image) x.image = "Add the post image.";
      if (!it.url.trim()) x.url = "Paste the Instagram post link.";
      else if (!INSTAGRAM_RE.test(it.url.trim())) x.url = "Use a link like https://www.instagram.com/p/XXXX/";
      if (Object.keys(x).length) e[it._key] = x;
    });
    setErrors(e);
    if (Object.keys(e).length) return toast.error("Please fix the highlighted post fields.");
    persist("instagram", { title, active, items: strip(items) }, { onSaved, onClose, setSaving, ok: "Instagram posts saved." });
  };
  return (
    <EditorModal title="Instagram posts" subtitle={`Up to ${limits.instagram} posts. Paste a public post link and we fetch the picture and caption — visitors are sent to the original post.`} onClose={onClose} onSave={save} saving={saving} size="xl" testId="editor-instagram">
      <div className="space-y-3">
        <SectionToggle label="Show this section" checked={active} onChange={setActive} hint="Hidden automatically when there is no active post." />
        <Field label="Section title" maxLength={60} placeholder={defaults.instagram} value={title} onChange={setTitle} t="section-title" />
        {items.length === 0 && <div className="rounded-lg border-2 border-dashed border-slate-300 py-6 text-center text-[13px] text-slate-500">No posts yet. Add a post, then paste its Instagram link.</div>}
        <SortableList items={items} onChange={setItems} keyOf={(i) => i._key} testId="instagram-list"
          renderItem={(it, idx, handle) => {
            const er = errors[it._key] || {};
            const loading = !!busy[it._key];
            return (
              <div className={`rounded-lg border bg-white p-2.5 grid grid-cols-12 gap-x-3 gap-y-2 ${Object.keys(er).length ? "border-red-300" : "border-slate-200"}`}>
                <div className="col-span-12 sm:col-span-1">{handle}</div>
                <div className="col-span-12 sm:col-span-3"><ImagePicker label="Post image" required value={it.image} onChange={(v) => patch(it._key, { image: v })} aspect="aspect-square" error={er.image} hint="Filled in automatically from the link, or upload your own." t={`insta-image-${idx}`} /></div>
                <div className="col-span-12 sm:col-span-6 grid gap-y-2 content-start">
                  <div>
                    <label className={labelCls}>Instagram post link <span className="text-red-500">*</span></label>
                    <div className="flex gap-1.5">
                      <input value={it.url} placeholder="https://www.instagram.com/p/…" data-testid={`insta-url-${idx}`} aria-invalid={er.url ? "true" : "false"}
                        onChange={(e) => patch(it._key, { url: e.target.value })}
                        onPaste={(e) => { const t = e.clipboardData.getData("text").trim(); if (INSTAGRAM_RE.test(t) && !it.image) { e.preventDefault(); patch(it._key, { url: t }); lookup(it._key, t); } }}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lookup(it._key, it.url); } }}
                        className={`${inputCls} ${er.url ? "!border-red-500" : ""}`} />
                      <button type="button" onClick={() => lookup(it._key, it.url)} disabled={loading || !it.url.trim()} data-testid={`insta-fetch-${idx}`} className={`${btnOutline} shrink-0`}>
                        {loading ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} {loading ? "Fetching…" : "Fetch details"}
                      </button>
                    </div>
                    {er.url ? <p role="alert" className="mt-1 text-[11px] text-red-600">{er.url}</p> : <p className="mt-1 text-[11px] text-slate-500">Paste the link — the picture and caption are fetched automatically (the post must be public).</p>}
                  </div>
                  <Field label="Caption" maxLength={150} value={it.caption} onChange={(v) => patch(it._key, { caption: v })} hint="Optional. Filled in from the post when available." t={`insta-caption-${idx}`} />
                </div>
                <div className="col-span-12 sm:col-span-2 flex sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-2">
                  <Toggle checked={it.active} onChange={(v) => patch(it._key, { active: v })} label="Active" />
                  <button type="button" onClick={() => { if (window.confirm("Delete this post?")) setItems(items.filter((i) => i._key !== it._key)); }} aria-label="Delete post" className="w-8 h-8 rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600 flex items-center justify-center"><Trash2 size={15} /></button>
                </div>
              </div>
            );
          }} />
        <button type="button" onClick={add} disabled={items.length >= limits.instagram} data-testid="instagram-add" className={btnOutline}><Plus size={14} /> Add post ({items.length}/{limits.instagram})</button>
      </div>
    </EditorModal>
  );
}

/* ------------------------------------------------------------ Social links */
export function SocialEditor({ section, onSaved, onClose }) {
  const [urls, setUrls] = useState(() => Object.fromEntries(NETWORKS.map((n) => [n.key, (section?.items || []).find((i) => i.network === n.key)?.url || ""])));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const save = () => {
    const e = {};
    NETWORKS.forEach((n) => {
      const v = urls[n.key].trim();
      if (v && !validSocialUrl(n.key, v)) e[n.key] = `Enter a secure link to your ${n.label} page, e.g. ${n.placeholder}`;
    });
    setErrors(e);
    if (Object.keys(e).length) return toast.error("Please fix the highlighted links.");
    const items = NETWORKS.filter((n) => urls[n.key].trim()).map((n) => ({ network: n.key, url: urls[n.key].trim() }));
    persist("social", { items }, { onSaved, onClose, setSaving, ok: "Social media links saved." });
  };

  return (
    <EditorModal title="Social media links" subtitle="Shown as icons in your storefront footer. Leave a field blank to hide that icon." onClose={onClose} onSave={save} saving={saving} testId="editor-social">
      <div className="space-y-3">
        {NETWORKS.map((n) => (
          <div key={n.key} className="flex items-start gap-3">
            <span className="mt-[26px] w-8 h-8 shrink-0 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center"><n.icon size={15} /></span>
            <Field className="flex-1" label={n.label} placeholder={n.placeholder} maxLength={500} value={urls[n.key]} error={errors[n.key]}
              onChange={(v) => { setUrls((u) => ({ ...u, [n.key]: v })); setErrors((x) => ({ ...x, [n.key]: undefined })); }} t={`social-${n.key}`} />
          </div>
        ))}
      </div>
    </EditorModal>
  );
}

/* ------------------------------------------------------------ Policy pages */
export function PoliciesEditor({ section, ctx, onSaved, onClose }) {
  const pages = ctx.policyPages;
  const max = ctx.limits.policy_chars;
  const [text, setText] = useState(() => Object.fromEntries(Object.keys(pages).map((k) => [k, section?.pages?.[k] || ""])));
  const [tab, setTab] = useState(Object.keys(pages)[0]);
  const [saving, setSaving] = useState(false);
  const save = () => persist("policies", { pages: text }, { onSaved, onClose, setSaving, ok: "Policy pages saved." });
  return (
    <EditorModal title="Policy pages" subtitle="Linked from the Information column of your storefront footer. Customers see the text exactly as typed (blank lines make paragraphs). A page left empty shows “not published yet”." onClose={onClose} onSave={save} saving={saving} size="xl" testId="editor-policies">
      <div className="flex gap-1.5 flex-wrap mb-3" role="tablist">
        {Object.entries(pages).map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`policy-tab-${k}`}
            className={`px-3 h-8 rounded-lg text-[12px] font-semibold border ${tab === k ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
            {label}{text[k].trim() && <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-green-400 align-middle" />}
          </button>
        ))}
      </div>
      <label className={labelCls}>{pages[tab]}</label>
      <textarea value={text[tab]} onChange={(e) => setText((t) => ({ ...t, [tab]: e.target.value }))} maxLength={max} rows={16} data-testid="policy-text"
        placeholder={`Write your ${pages[tab]} here…`} className={`${inputCls} !h-auto py-2 leading-relaxed`} />
      <div className="mt-1 text-[11px] text-slate-500 text-right">{text[tab].length}/{max}</div>
      <a href={`/store/${ctx.slug}/${tab}`} target="_blank" rel="noreferrer" className="text-[12px] text-blue-600 hover:underline">Open this page on your store (save first)</a>
    </EditorModal>
  );
}


/* ----------------------------------------------------- Online payments (Razorpay) */
const KEY_RE = /^rzp_(test|live)_[A-Za-z0-9]{8,40}$/;
export function PaymentsEditor({ section, onSaved, onClose }) {
  const [enabled, setEnabled] = useState(section?.key_id ? section.enabled : true);
  const [keyId, setKeyId] = useState(section?.key_id || "");
  const [secret, setSecret] = useState("");
  const [verify, setVerify] = useState(true);
  const [cod, setCod] = useState(section?.cod_enabled !== false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const saved = !!section?.key_id;
  const codOn = cod || !enabled;                       // Cash on Delivery stays on whenever online payments are off

  const save = async () => {
    const e = {};
    if (!keyId.trim()) { if (enabled) e.keyId = "Enter your Razorpay Key ID."; }
    else if (!KEY_RE.test(keyId.trim())) e.keyId = "Looks like rzp_test_XXXXXXXXXXXX or rzp_live_XXXXXXXXXXXX.";
    if (enabled && !secret.trim() && (!section?.secret_set || keyId.trim() !== section.key_id)) e.secret = "Enter the Key Secret.";
    else if (secret.trim() && secret.trim().length < 12) e.secret = "That secret looks too short.";
    setErrors(e);
    if (Object.keys(e).length) return toast.error("Please fix the highlighted fields.");
    if (enabled && keyId.trim().startsWith("rzp_live_") && !window.confirm("These are LIVE keys. Customers' online payments will be charged for real and settle to your Razorpay account. Continue?")) return;
    setSaving(true);
    try {
      const putRazorpay = () => api.put("/vendor/payments/razorpay", { key_id: keyId.trim() || null, key_secret: secret.trim() || null, enabled, verify });
      const putCod = () => api.put("/vendor/payments/cod", { enabled: codOn });
      if (codOn) { await putCod(); await putRazorpay(); } else { await putRazorpay(); await putCod(); }   // never leave a moment with no way to pay
      toast.success(!codOn ? "Cash on Delivery is off. Customers can pay online only." : enabled ? "Online payments are on. Customers can now pay with Razorpay." : "Online payments are off. Customers will see Cash on Delivery only.");
      onSaved(); onClose();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async () => {
    if (!window.confirm("Remove your saved Razorpay keys? Customers will see Cash on Delivery only.")) return;
    try { await api.delete("/vendor/payments/razorpay"); toast.success("Razorpay keys removed."); onSaved(); onClose(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  return (
    <EditorModal title="Online payments (Razorpay)" subtitle="Customers pay straight into your own Razorpay account. Get the keys from the Razorpay Dashboard → Account & Settings → API Keys." onClose={onClose} onSave={save} saving={saving} testId="editor-payments">
      <div className="space-y-3">
        <SectionToggle label="Accept online payments" checked={enabled} onChange={setEnabled} hint={enabled ? "Customers can choose Pay Online or Cash on Delivery." : "Off: customers see Cash on Delivery only."} />
        <SectionToggle label="Accept Cash on Delivery" checked={codOn} onChange={(v) => enabled ? setCod(v) : toast.error("Turn on online payments first — customers need at least one way to pay.")}
          hint={codOn ? "Customers can choose Cash on Delivery." : "Off: customers must pay online."} />
        <Field label="Key ID" placeholder="rzp_test_XXXXXXXXXXXX" value={keyId} onChange={setKeyId} error={errors.keyId} t="payments-key-id" autoComplete="off" />
        <Field label="Key Secret" type="password" value={secret} onChange={setSecret} error={errors.secret} t="payments-key-secret" autoComplete="new-password"
          placeholder={section?.secret_set ? `${section.secret_hint} (saved — leave blank to keep)` : "Paste your Key Secret"} hint="Stored encrypted. It is never shown again after saving." />
        <label className="flex items-center gap-2 text-[12px] text-slate-600">
          <input type="checkbox" checked={verify} onChange={(e) => setVerify(e.target.checked)} data-testid="payments-verify" className="rounded border-slate-300" />
          Check the keys with Razorpay before saving
        </label>
        <p className="text-[11px] text-slate-500">Use <b>test</b> keys to try the flow first (no real money moves), then switch to <b>live</b> keys.</p>
        {saved && <button type="button" onClick={remove} data-testid="payments-remove" className={`${btnOutline} !text-red-600`}><Trash2 size={14} /> Remove saved keys</button>}
      </div>
    </EditorModal>
  );
}


/* ------------------------------------------------------------------ Tax per category */
export function TaxEditor({ onSaved, onClose }) {
  const [rows, setRows] = useState(null);
  const [types, setTypes] = useState(["GST", "VAT"]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/vendor/tax").then(({ data }) => { setRows(data.rows.map((r) => ({ ...r, rate: r.rate == null ? "" : String(r.rate) }))); setTypes(data.types); })
      .catch((err) => { toast.error(apiErr(err.response?.data?.detail)); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const patch = (cat, p) => { setRows((all) => all.map((r) => (r.category === cat ? { ...r, ...p } : r))); setErrors((e) => ({ ...e, [cat]: undefined })); };
  const save = async () => {
    const e = {};
    rows.forEach((r) => {
      if (!r.type) { if (r.rate !== "" && Number(r.rate) !== 0) e[r.category] = "Choose GST or VAT."; return; }
      if (r.rate === "" || !(Number(r.rate) >= 0) || Number(r.rate) > 100) e[r.category] = "Enter a % between 0 and 100.";
      else if (!/^\d+(\.\d{1,2})?$/.test(String(r.rate))) e[r.category] = "At most 2 decimal places.";
    });
    setErrors(e);
    if (Object.keys(e).length) return toast.error("Please fix the highlighted rows.");
    setSaving(true);
    try {
      await api.put("/vendor/tax", { rows: rows.map((r) => ({ category: r.category, type: r.type || null, rate: r.type ? Number(r.rate) : null })) });
      toast.success("Tax settings saved. They apply to new carts and orders.");
      onSaved(); onClose();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <EditorModal title="Tax" subtitle="Choose GST or VAT and the tax % for each product category. The same tax is used in the POS and on your storefront checkout, added on top of the item price." onClose={onClose} onSave={save} saving={saving} size="lg" testId="editor-tax">
      {!rows ? <p className="text-[13px] text-slate-500 py-6 text-center">Loading categories…</p> : rows.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-slate-300 py-8 text-center text-[13px] text-slate-500" data-testid="tax-empty">No product categories yet. Add products with a category first.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]" data-testid="tax-table">
            <thead><tr className="text-left text-slate-500 text-[12px]"><th className="py-2 pr-3 font-semibold">Category</th><th className="py-2 pr-3 font-semibold w-[140px]">Tax</th><th className="py-2 font-semibold w-[130px]">Tax %</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.category} className="border-t border-slate-100 align-top" data-testid={`tax-row-${r.category}`}>
                  <td className="py-2.5 pr-3 font-semibold text-neutral-900">{r.category}</td>
                  <td className="py-2 pr-3">
                    <select value={r.type} onChange={(e) => patch(r.category, { type: e.target.value, rate: e.target.value ? r.rate : "" })} aria-label={`Tax type for ${r.category}`} data-testid={`tax-type-${r.category}`} className={inputCls}>
                      <option value="">No tax set</option>{types.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select></td>
                  <td className="py-2">
                    <div className="flex items-center gap-1.5"><input type="number" min="0" max="100" step="0.01" value={r.rate} disabled={!r.type} placeholder="0" onChange={(e) => patch(r.category, { rate: e.target.value })}
                      aria-label={`Tax percentage for ${r.category}`} data-testid={`tax-rate-${r.category}`} className={`${inputCls} ${errors[r.category] ? "!border-red-500" : ""} disabled:bg-slate-50`} /><span className="text-slate-500">%</span></div>
                    {errors[r.category] && <p role="alert" className="mt-1 text-[11px] text-red-600">{errors[r.category]}</p>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-3 text-[11px] text-slate-500">A category with no tax set uses each product&apos;s own GST % (Products → edit).</p>
        </div>
      )}
    </EditorModal>
  );
}
